import React, { useMemo, useState } from "react";

import { displayPhone, formatDateCell, startCase } from "../../lib/utils";

function FollowUpSection({
  emptyCopy,
  followUps,
  viewMode,
  onLogCall,
  onMarkComplete,
  onOpenJourney,
  roleScope,
  title,
}) {
  return (
    <section className="report-card">
      <div className="report-card-header">
        <div>
          <h2>{title}</h2>
          <p>{emptyCopy}</p>
        </div>
      </div>

      {followUps.length ? (
        viewMode === "cards" ? (
          <div className="follow-up-card-grid">
            {followUps.map((followUp) => (
              <article className="follow-up-card" key={followUp.id}>
                <div className="follow-up-card-topline">
                  <span className="journey-badge">{startCase(followUp.status)}</span>
                  <strong>{formatDateCell(followUp.scheduled_for)}</strong>
                </div>

                <div className="follow-up-card-head">
                  <h3>{followUp.lead_full_name}</h3>
                  <p>
                    {followUp.lead_client_id} · {displayPhone(followUp, roleScope)}
                  </p>
                </div>

                <div className="follow-up-card-meta">
                  <span>Source: {followUp.lead_source_name || "—"}</span>
                  <span>Status: {startCase(followUp.lead_status)}</span>
                  <span>
                    {followUp.lead_city_name || "Unspecified City"} / {followUp.lead_region_name || "Unspecified Region"}
                  </span>
                  <span>Agent: {followUp.assigned_to_name || "Unassigned"}</span>
                </div>

                <p className="follow-up-card-copy">
                  {followUp.title || "Follow-up"}: {followUp.notes || "No notes added yet."}
                </p>

                <div className="follow-up-card-actions">
                  <button
                    type="button"
                    className="secondary-ghost compact-button"
                    onClick={() => onOpenJourney(followUp.lead)}
                  >
                    Open Journey
                  </button>
                  <button
                    type="button"
                    className="secondary-ghost compact-button"
                    onClick={() => onLogCall(followUp.lead)}
                  >
                    Log Call
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="table-scroll report-table-scroll">
            <table className="report-table follow-up-list-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Schedule</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Agent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {followUps.map((followUp) => (
                  <tr key={followUp.id}>
                    <td>
                      <strong>{followUp.lead_full_name}</strong>
                      <div className="subtext-cell">
                        {followUp.lead_client_id} · {displayPhone(followUp, roleScope)}
                      </div>
                    </td>
                    <td>{formatDateCell(followUp.scheduled_for)}</td>
                    <td>{followUp.lead_source_name || "—"}</td>
                    <td>{startCase(followUp.lead_status)}</td>
                    <td>
                      {followUp.lead_city_name || "Unspecified City"} / {followUp.lead_region_name || "Unspecified Region"}
                    </td>
                    <td>{followUp.assigned_to_name || "Unassigned"}</td>
                    <td>
                      <div className="follow-up-inline-actions">
                        <button
                          type="button"
                          className="secondary-ghost compact-button"
                          onClick={() => onOpenJourney(followUp.lead)}
                        >
                          Journey
                        </button>
                        <button
                          type="button"
                          className="secondary-ghost compact-button"
                          onClick={() => onLogCall(followUp.lead)}
                        >
                          Call
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="table-state">No follow-ups in this bucket.</div>
      )}
    </section>
  );
}

function FollowUpsBoard({ followUps, onLogCall, onMarkComplete, onOpenJourney, roleScope }) {
  const [viewMode, setViewMode] = useState("cards");
  const viewModeLabel = viewMode === "cards" ? "Cards" : "Lists";

  const sections = useMemo(() => {
    const now = new Date();
    const todayKey = now.toDateString();
    const buckets = {
      overdue: [],
      today: [],
      upcoming: [],
    };

    followUps.forEach((followUp) => {
      const scheduledAt = new Date(followUp.scheduled_for);
      if (scheduledAt < now) {
        const noPriorContact = followUp.lead_status === "new";
        if (noPriorContact) {
          buckets.overdue.push(followUp);
        } else {
          buckets.today.push(followUp);
        }
        return;
      }
      if (scheduledAt.toDateString() === todayKey) {
        buckets.today.push(followUp);
        return;
      }
      buckets.upcoming.push(followUp);
    });

    buckets.overdue.sort(
      (left, right) => new Date(left.scheduled_for).getTime() - new Date(right.scheduled_for).getTime(),
    );
    buckets.today.sort(
      (left, right) => new Date(left.scheduled_for).getTime() - new Date(right.scheduled_for).getTime(),
    );
    buckets.upcoming.sort(
      (left, right) => new Date(left.scheduled_for).getTime() - new Date(right.scheduled_for).getTime(),
    );

    return [
      {
        key: "overdue",
        title: "Overdue",
        emptyCopy: "Overdue follow-ups that should be handled first.",
        priority: 0,
        items: buckets.overdue,
      },
      {
        key: "today",
        title: "Today",
        emptyCopy: "Follow-ups scheduled for today.",
        priority: 1,
        items: buckets.today,
      },
      {
        key: "upcoming",
        title: "Upcoming",
        emptyCopy: "Upcoming follow-ups that are already scheduled.",
        priority: 2,
        items: buckets.upcoming,
      },
    ].sort((left, right) => {
      if (left.items.length && !right.items.length) {
        return -1;
      }
      if (!left.items.length && right.items.length) {
        return 1;
      }
      return left.priority - right.priority;
    });
  }, [followUps]);

  const overdueSection = sections.find((section) => section.key === "overdue");
  const todaySection = sections.find((section) => section.key === "today");
  const upcomingSection = sections.find((section) => section.key === "upcoming");

  return (
    <section className="analytics-shell">
      <section className="toolbar-panel">
        <div className="toolbar-header">
          <h1>Follow-ups</h1>
          <div className="follow-up-toolbar-actions">
            <span className="follow-up-priority-note">Non-empty buckets appear first. Urgency order remains Overdue → Today → Upcoming.</span>
            <div className="view-switch">
              <button
                type="button"
                className={`view-switch-button ${viewMode === "cards" ? "active" : ""}`}
                onClick={() => setViewMode("cards")}
              >
                Cards
              </button>
              <button
                type="button"
                className={`view-switch-button ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                Lists
              </button>
            </div>
          </div>
        </div>
        <p className="follow-up-board-copy">
          All pending callback work items are grouped here so agents can action them quickly. The most urgent items are shown first.
        </p>
      </section>

      <section className="analytics-summary-grid reports-summary-grid">
        <article className="analytics-summary-card highlighted">
          <span>Pending</span>
          <strong>
            {(overdueSection?.items.length || 0) +
              (todaySection?.items.length || 0) +
              (upcomingSection?.items.length || 0)}
          </strong>
        </article>
        <article className="analytics-summary-card">
          <span>Overdue</span>
          <strong>{overdueSection?.items.length || 0}</strong>
        </article>
        <article className="analytics-summary-card">
          <span>Today</span>
          <strong>{todaySection?.items.length || 0}</strong>
        </article>
        <article className="analytics-summary-card">
          <span>Upcoming</span>
          <strong>{upcomingSection?.items.length || 0}</strong>
        </article>
        <article className="analytics-summary-card">
          <span>Board View</span>
          <strong>{viewModeLabel}</strong>
        </article>
      </section>

      {sections.map((section) => (
        <FollowUpSection
          key={section.key}
          emptyCopy={section.emptyCopy}
          followUps={section.items}
          viewMode={viewMode}
          onLogCall={onLogCall}
          onMarkComplete={onMarkComplete}
          onOpenJourney={onOpenJourney}
          roleScope={roleScope}
          title={section.title}
        />
      ))}
    </section>
  );
}

export default FollowUpsBoard;
